import { createAsyncThunk, createSlice, Dispatch } from '@reduxjs/toolkit';
import Plan from 'model/Plan';
import PlanReference from 'model/PlanReference';
import {
  getAllHRAAscociatedDentals,
  getAllHRAAscociatedMedicals,
  getAllHRAAscociatedVisions,
  getAllTaxAdvantagedPlans,
  getTaxAdvantagedPlanList,
} from 'modules/benefitsWellness/services/TaxAdvantagedPlanService';
import * as BenGuideService from 'services/BenGuideService';
import * as PlanService from 'modules/auth/services/PlanService';
import AddPlanApiRequestPayload from 'model/AddPlanApiRequestPayload';
import SectionApiRequestPayload from 'model/SectionApiRequestPayload';
import SectionApiRequestResponse from 'model/SectionApiRequestResponse';
import TaxAdvantageAccountsPlan from 'model/TaxAdvantageAccountsPlan';
import {
  HRA_PLAN_CONTENT_TABS,
  TaxAdvantagedSection,
} from 'modules/taxAdvantaged/constants/taxAdvantagedConstants';
import { SectionName } from 'modules/home/constants';
import {
  GuideGetState,
  readGuidePlans,
} from 'modules/effectiveDate/utils/datedPlanList';

interface TaxAdvantagedState {
  planList: {
    inProgress: boolean;
    data: Plan[];
  };
  hraSection: {
    enabled: boolean;
    plans: PlanReference[];
  };
  selectedPlans: {
    data: TaxAdvantageAccountsPlan[];
    inProgress: boolean;
  };
  defaultRichMedia: {
    inProgress: boolean;
    error: any;
    data: {
      [key in string]: string;
    };
  };
  inProgress: boolean;
  associatedMedicalPlans: {
    inProgress: boolean;
    data: any;
  };
  associatedDentalPlans: {
    inProgress: boolean;
    data: any;
  };
  associatedVisionPlans: {
    inProgress: boolean;
    data: any;
  };
}

const initialState: TaxAdvantagedState = {
  planList: {
    inProgress: false,
    data: [],
  },
  hraSection: {
    enabled: false,
    plans: [],
  },
  selectedPlans: {
    data: [],
    inProgress: false,
  },
  inProgress: false,
  defaultRichMedia: {
    inProgress: false,
    error: null,
    data: {
      [SectionName.HRA_OVERVIEW_TEXT]: '',
      [SectionName.HRA_ELIGIBILITY_TEXT]: '',
      [SectionName.HRA_EXPENSES_TEXT]: '',
      [SectionName.HRA_ADVANTAGES_TEXT]: '',
      [SectionName.HRA_CONSIDERATIONS_TEXT]: '',
    },
  },
  associatedMedicalPlans: {
    inProgress: false,
    data: null,
  },
  associatedDentalPlans: {
    inProgress: false,
    data: null,
  },
  associatedVisionPlans: {
    inProgress: false,
    data: null,
  },
};

type PlanFetchData = {
  employerId: string;
  planYearId: string;
  benefitClasses: string[];
};

type SelectPlanParams = {
  benguideId: string;
  addPlansPayload: AddPlanApiRequestPayload;
  benefitKind: string;
};

export const fetchHRAPlans = createAsyncThunk<any, PlanFetchData>(
  'hra/fetchHRAPlans',
  async (
    { employerId, planYearId, benefitClasses },
    { dispatch, getState }
  ) => {
    const response = await readGuidePlans(
      dispatch,
      getState as GuideGetState,
      employerId,
      planYearId,
      (planYear) =>
        getTaxAdvantagedPlanList(
          employerId,
          planYear,
          [TaxAdvantagedSection.HRA],
          benefitClasses
        )
    );
    return response.data.content;
  }
);

export const selectHRAPlans = createAsyncThunk<
  SectionApiRequestPayload,
  SelectPlanParams
>('hra/selectHRAPlans', async ({ benguideId, addPlansPayload }) => {
  await BenGuideService.addPlans(benguideId, addPlansPayload);
  const response = await BenGuideService.getBenefitPageSection(
    benguideId,
    TaxAdvantagedSection.HRA
  );
  return response.data;
});

export const getHRASection = createAsyncThunk<
  SectionApiRequestResponse,
  { benguideId: string }
>('hra/getBenefitSection', async ({ benguideId }) => {
  const response = await BenGuideService.getBenefitPageSection(
    benguideId,
    TaxAdvantagedSection.HRA
  );
  return response.data;
});

export const getPlansFromPlanRefs = createAsyncThunk<
  TaxAdvantageAccountsPlan[],
  { planRefs: PlanReference[] }
>('hra/getPlansFromPlanRefs', async ({ planRefs }) => {
  const planPromises = planRefs.map((plan) => {
    const { planId, revision } = plan;
    return PlanService.getPlans('tax-advantage-accounts', planId, revision);
  });
  const promiseResponses = await Promise.all(planPromises);
  return promiseResponses.map((plan) => plan.data);
});

const hraSlice = createSlice({
  name: 'hra',
  initialState,
  reducers: {
    getDefaultMediaStarted(state) {
      state.defaultRichMedia.inProgress = true;
    },
    getDefaultMediaSuccess(state, { payload }) {
      state.defaultRichMedia.inProgress = false;
      state.defaultRichMedia.data[payload.section] = payload.media.content;
    },
    getDefaultMediaFailed(state, payload) {
      state.defaultRichMedia.inProgress = false;
      state.defaultRichMedia.error = payload;
    },
    updateHRASection(state, { payload }) {
      state.hraSection = payload;
    },
    listAllHRAPlansStarted(state) {
      state.planList.inProgress = true;
    },
    listAllHRAPlansSuccess(state, { payload }) {
      state.planList.inProgress = false;
      state.planList.data = payload;
    },
    listAllHRAPlansFailed(state) {
      state.planList.inProgress = false;
    },
    associatedMedicalPlansStarted(state) {
      state.associatedMedicalPlans.inProgress = true;
    },
    associatedMedicalPlansSuccess(state, { payload }) {
      state.associatedMedicalPlans.inProgress = true;
      state.associatedMedicalPlans.data = payload;
    },
    associatedMedicalPlansFailed(state) {
      state.associatedMedicalPlans.inProgress = false;
    },
    associatedDentalPlansStarted(state) {
      state.associatedDentalPlans.inProgress = true;
    },
    associatedDentalPlansSuccess(state, { payload }) {
      state.associatedDentalPlans.inProgress = true;
      state.associatedDentalPlans.data = payload;
    },
    associatedDentalPlansFailed(state) {
      state.associatedDentalPlans.inProgress = false;
    },
    associatedVisionPlansStarted(state) {
      state.associatedVisionPlans.inProgress = true;
    },
    associatedVisionPlansSuccess(state, { payload }) {
      state.associatedVisionPlans.inProgress = true;
      state.associatedVisionPlans.data = payload;
    },
    associatedVisionPlansFailed(state) {
      state.associatedVisionPlans.inProgress = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHRAPlans.pending, (state) => {
      state.planList.inProgress = true;
    });
    builder.addCase(fetchHRAPlans.fulfilled, (state, { payload }) => {
      state.planList.inProgress = false;
      state.planList.data = payload;
    });
    builder.addCase(selectHRAPlans.pending, (state) => {
      state.selectedPlans.inProgress = true;
    });
    builder.addCase(selectHRAPlans.fulfilled, (state, { payload }) => {
      state.selectedPlans.inProgress = false;
      state.hraSection = payload.benefitPage;
    });
    builder.addCase(getHRASection.pending, (state) => {
      state.inProgress = true;
    });
    builder.addCase(getHRASection.fulfilled, (state, { payload }) => {
      state.inProgress = false;
      state.hraSection = payload.benefitPage;
    });
    builder.addCase(getPlansFromPlanRefs.pending, (state) => {
      state.selectedPlans.inProgress = true;
    });
    builder.addCase(getPlansFromPlanRefs.fulfilled, (state, { payload }) => {
      state.selectedPlans.inProgress = false;
      state.selectedPlans.data = payload;
    });
  },
});

export const {
  updateHRASection,
  getDefaultMediaStarted,
  getDefaultMediaSuccess,
  getDefaultMediaFailed,
  listAllHRAPlansStarted,
  listAllHRAPlansSuccess,
  listAllHRAPlansFailed,
  associatedMedicalPlansStarted,
  associatedMedicalPlansSuccess,
  associatedMedicalPlansFailed,
  associatedDentalPlansStarted,
  associatedDentalPlansSuccess,
  associatedDentalPlansFailed,
  associatedVisionPlansStarted,
  associatedVisionPlansSuccess,
  associatedVisionPlansFailed,
} = hraSlice.actions;

export default hraSlice.reducer;

export const listHRAPlans = (
  employerId: string,
  planYearId: string,
  benefitClasses: string[],
  allowHidden?: boolean
) => {
  return async (dispatch: Dispatch, getState: GuideGetState) => {
    dispatch(listAllHRAPlansStarted());
    try {
      const response = await readGuidePlans(
        dispatch,
        getState,
        employerId,
        planYearId,
        (planYear) =>
          getAllTaxAdvantagedPlans(
            employerId,
            planYear,
            ['HRA'],
            benefitClasses,
            allowHidden
          ),
        allowHidden ? { hiddenPlansOf: 'HRA' } : undefined
      );
      dispatch(listAllHRAPlansSuccess(response.data));
    } catch (error) {
      dispatch(listAllHRAPlansFailed());
    }
  };
};

export const getDefaultMediaForHRA = (benguideId: string, planId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(getDefaultMediaStarted());
    HRA_PLAN_CONTENT_TABS.forEach((section) => {
      BenGuideService.getRichMediaContentForPlan(
        benguideId,
        section.code,
        planId,
        true
      )
        .then(({ data }) => {
          dispatch(
            getDefaultMediaSuccess({
              section: section.code,
              media: data,
            })
          );
        })
        .catch((error) => {
          dispatch(getDefaultMediaFailed(error));
        });
    });
  };
};

export const listHRAAssociatedMedicalPlans = (
  employerId: string,
  planYearId: string
) => {
  return async (dispatch: Dispatch, getState: GuideGetState) => {
    dispatch(associatedMedicalPlansStarted());
    try {
      const response = await readGuidePlans(
        dispatch,
        getState,
        employerId,
        planYearId,
        (planYear) => getAllHRAAscociatedMedicals(employerId, planYear)
      );
      dispatch(associatedMedicalPlansSuccess(response.data));
    } catch (error) {
      dispatch(associatedMedicalPlansFailed());
    }
  };
};

export const listHRAAssociatedDentalPlans = (
  employerId: string,
  planYearId: string
) => {
  return async (dispatch: Dispatch, getState: GuideGetState) => {
    dispatch(associatedDentalPlansStarted());
    try {
      const response = await readGuidePlans(
        dispatch,
        getState,
        employerId,
        planYearId,
        (planYear) => getAllHRAAscociatedDentals(employerId, planYear)
      );
      dispatch(associatedDentalPlansSuccess(response.data));
    } catch (error) {
      dispatch(associatedDentalPlansFailed());
    }
  };
};

export const listHRAAssociatedVisionPlans = (
  employerId: string,
  planYearId: string
) => {
  return async (dispatch: Dispatch, getState: GuideGetState) => {
    dispatch(associatedVisionPlansStarted());
    try {
      const response = await readGuidePlans(
        dispatch,
        getState,
        employerId,
        planYearId,
        (planYear) => getAllHRAAscociatedVisions(employerId, planYear)
      );
      dispatch(associatedVisionPlansSuccess(response.data));
    } catch (error) {
      dispatch(associatedVisionPlansFailed());
    }
  };
};
